import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { CustomConfirmDialog } from '../../../../../../../components/dialogs'
import { useToastContext } from '../../../../../../../contexts'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import {
  requestDeleteCompanyCode,
  requestDeleteCompanyCodes,
  useCompanyCodeById,
} from '../../../data'

export const DeleteCompanyCodes: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const { companyCodeIds } = useParams()

  const companyCodeIdsToDelete: string[] = companyCodeIds?.split(',') || []
  const companyCodeId =
    companyCodeIdsToDelete.length === 1 ? companyCodeIdsToDelete[0] : null

  const { data: companyCode, isLoading: isLoadingCompanyCodeById } =
    useCompanyCodeById(companyCodeId)

  const handleDelete = async () => {
    try {
      setIsRequestRunning(true)

      await submitDelete()

      handleClose()

      showToast({
        severity: 'success',
        summary: t(
          'masterDataManagement.companyCodes.table.delete.dialog.delete.messages.success.summary',
        ),
        detail: t(
          'masterDataManagement.companyCodes.table.delete.dialog.delete.messages.success.detail',
          {
            count: companyCodeIdsToDelete?.length,
            companyCode: companyCode?.Common.Company,
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'masterDataManagement.companyCodes.table.delete.dialog.delete.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const submitDelete = async () => {
    if (companyCodeIdsToDelete.length === 1) {
      await requestDeleteCompanyCode(companyCodeIdsToDelete[0])
    } else {
      await requestDeleteCompanyCodes(companyCodeIdsToDelete)
    }
  }

  const handleClose = () => {
    navigate('/master-data-management/company-codes')
  }

  return (
    <CustomConfirmDialog
      severity="danger"
      isVisible
      header={t('masterDataManagement.companyCodes.table.delete.dialog.title', {
        count: companyCodeIdsToDelete?.length,
      })}
      message={t(
        'masterDataManagement.companyCodes.table.delete.dialog.message',
        {
          count: companyCodeIdsToDelete?.length,
          companyCode: companyCode?.Common.Company,
        },
      )}
      onHide={handleClose}
      onReject={handleClose}
      rejectLabel={t(
        'masterDataManagement.companyCodes.table.delete.dialog.cancel.label',
      )}
      onAccept={handleDelete}
      acceptLabel={t(
        'masterDataManagement.companyCodes.table.delete.dialog.delete.label',
      )}
    />
  )
}
